'use client';

import { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { toast } from 'sonner';

export default function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const router = useRouter();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = async (searchQuery) => {
    if (searchQuery.trim().length < 2) {
      setResults(null);
      setShowResults(false);
      return;
    }

    setLoading(true);
    setShowResults(true);
    try {
      const res = await fetch(`/api/search?q=${encodeURIComponent(searchQuery)}`);
      if (!res.ok) throw new Error('Search failed');
      const data = await res.json();
      setResults(data);
    } catch (error) {
      console.error('Error searching:', error);
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    if (value.trim().length >= 2) {
      handleSearch(value);
    } else {
      setResults(null);
      setShowResults(false);
    }
  };

  const handleResultClick = (type, id) => {
    setShowResults(false);
    setQuery('');
    if (type === 'class') {
      router.push(`/admin/classes`);
    } else if (type === 'instructor') {
      router.push(`/admin/instructors`);
    } else if (type === 'booking') {
      router.push(`/admin/classes`);
    }
  };

  return (
    <div className="relative" ref={searchRef}>
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={handleQueryChange}
          placeholder="Search classes, instructors, bookings..."
          className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] text-gray-900 bg-white"
        />
        <svg
          className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>

      {showResults && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-gray-600">Searching...</div>
          ) : results ? (
            <div className="p-2">
              {results.classes.length > 0 && (
                <div className="mb-4">
                  <h3 className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase">Classes</h3>
                  {results.classes.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleResultClick('class', item.id)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <div className="font-semibold text-gray-900">{item.className}</div>
                      <div className="text-sm text-gray-600">
                        {item.instructor} • {item.date && format(new Date(item.date), 'MMM d, yyyy')}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {results.instructors.length > 0 && (
                <div className="mb-4">
                  <h3 className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase">Instructors</h3>
                  {results.instructors.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleResultClick('instructor', item.id)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <div className="font-semibold text-gray-900">{item.name}</div>
                      {item.email && (
                        <div className="text-sm text-gray-600">{item.email}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {results.bookings.length > 0 && (
                <div className="mb-4">
                  <h3 className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase">Bookings</h3>
                  {results.bookings.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleResultClick('booking', item.id)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <div className="font-semibold text-gray-900">{item.className}</div>
                      <div className="text-sm text-gray-600">
                        {item.date && format(new Date(item.date), 'MMM d, yyyy')} • {item.status}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {results.classes.length === 0 &&
                results.instructors.length === 0 &&
                results.bookings.length === 0 && (
                  <div className="p-4 text-center text-gray-600">No results found</div>
                )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

